import { clsx } from "clsx";
import { type FC, useEffect } from "react";
import { type FallbackProps, useErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useRouteError } from "react-router";
import { Link } from "react-router-dom";

import { ActionButton, usePrevious } from "widgets";

import cssStyle from "@/App.module.css";
import style from "@/ui-modules/core/styles/App.module.scss";

import InformationPage from "./InformationPage.js";
import { type Configuration } from "@/graphql.ts";
import { downloadLogArchive } from "@/redux/modules/data-export.ts";
import { logApplicationError } from "@/redux/modules/errors.ts";
import { type IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";

// error page for the full application, including all initialization logic and navbar
export const RouterRuntimeException: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();

  const error = useRouteError();

  useEffect(() => {
    // keep the error in the application log so that it is available to investigate the details in /errors page
    dispatch(logApplicationError(error));
  }, []);

  return (
    <div className={clsx(style.app, cssStyle.app, "root")}>
      <RuntimeExceptionPage onRefresh={() => window.location.reload()} />
    </div>
  );
};

// error page for the main content, leaving the navbar alive

export const RuntimeException: FC<FallbackProps> = ({ error }) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const location = useLocation();
  const { resetBoundary } = useErrorBoundary();
  const previousLocation = usePrevious(location.pathname);

  useEffect(() => {
    // keep the error in the application log so that it is available to investigate the details in /errors page
    dispatch(logApplicationError(error));
  }, []);

  useEffect(() => {
    if (previousLocation && previousLocation !== location.pathname) {
      resetBoundary();
    }
  }, [location.pathname]);

  return <RuntimeExceptionPage onRefresh={resetBoundary} />;
};

const RuntimeExceptionPage: FC<{ onRefresh: () => void }> = ({ onRefresh }) => {
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration);
  const dispatch = useDispatch<ReduxDispatch>();
  const contactEmail = <a href={`mailto:${configuration?.application?.helpdeskEmail}`}>administrator</a>;

  return (
    <InformationPage errorTitle={`Sorry, an error occurred while processing your request.`} noGoBackLink showReloadLink>
      <div style={{ display: "flex", flexDirection: "column", rowGap: 15, alignItems: "center" }}>
        <div>
          For more details, please go to the <Link to={`/errors`}>error page</Link>.
        </div>
        <div>
          To follow up on this issue, please{" "}
          <a onClick={() => void dispatch(downloadLogArchive())}>download the error report</a> and send it to your{" "}
          {contactEmail}.
        </div>
        <ActionButton icon={"refresh"} label={"Try again"} onPress={onRefresh} />
      </div>
    </InformationPage>
  );
};
